import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowDown, ArrowLeft, ArrowUp, ImagePlus, Star, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/gestion/habitaciones/$id")({
  component: HabitacionEditor,
});

type Habitacion = {
  id?: string;
  nombre: string;
  descripcion: string;
  capacidad: number;
  precio_temporada_alta: number;
  precio_temporada_media: number;
  precio_temporada_baja: number;
  activa: boolean;
  imagen_url: string | null;
  orden: number;
};

const empty: Habitacion = {
  nombre: "",
  descripcion: "",
  capacidad: 2,
  precio_temporada_alta: 0,
  precio_temporada_media: 0,
  precio_temporada_baja: 0,
  activa: true,
  imagen_url: "",
  orden: 0,
};

function HabitacionEditor() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const isNew = id === "nueva";
  const [data, setData] = useState<Habitacion | null>(isNew ? { ...empty } : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      const { data: row, error } = await supabase.from("habitaciones").select("*").eq("id", id).maybeSingle();
      if (error) return toast.error(error.message);
      if (!row) { toast.error("No encontrada"); navigate({ to: "/gestion/habitaciones" }); return; }
      setData(row as Habitacion);
    })();
  }, [id, isNew]);

  async function save() {
    if (!data) return;
    setSaving(true);
    try {
      const payload = {
        nombre: data.nombre,
        descripcion: data.descripcion ?? "",
        capacidad: Number(data.capacidad),
        precio_temporada_alta: Number(data.precio_temporada_alta),
        precio_temporada_media: Number(data.precio_temporada_media),
        precio_temporada_baja: Number(data.precio_temporada_baja),
        activa: !!data.activa,
        imagen_url: data.imagen_url || null,
        orden: Number(data.orden ?? 0),
      };
      if (isNew) {
        const { data: created, error } = await supabase.from("habitaciones").insert(payload).select("id").single();
        if (error) throw error;
        toast.success("Habitación creada");
        navigate({ to: "/gestion/habitaciones/$id", params: { id: created!.id } });
      } else {
        const { error } = await supabase.from("habitaciones").update(payload).eq("id", id);
        if (error) throw error;
        toast.success("Cambios guardados");
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (!data) return <p className="text-sm text-muted-foreground">Cargando…</p>;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" asChild>
          <Link to="/gestion/habitaciones"><ArrowLeft className="mr-2 h-4 w-4" />Volver</Link>
        </Button>
        <Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar cambios"}</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-serif">Datos básicos</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Field label="Nombre"><Input value={data.nombre} onChange={(e) => setData({ ...data, nombre: e.target.value })} /></Field>
          <Field label="Descripción"><Textarea rows={4} value={data.descripcion} onChange={(e) => setData({ ...data, descripcion: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Capacidad"><Input type="number" min={1} value={data.capacidad} onChange={(e) => setData({ ...data, capacidad: Number(e.target.value) })} /></Field>
            <Field label="Orden"><Input type="number" value={data.orden} onChange={(e) => setData({ ...data, orden: Number(e.target.value) })} /></Field>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label>Visible en la web</Label>
              <p className="text-xs text-muted-foreground">Si la desactivas no aparecerá en el buscador público.</p>
            </div>
            <Switch checked={!!data.activa} onCheckedChange={(v) => setData({ ...data, activa: v })} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">Precios base por noche</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Los precios se gestionan en <Link to="/gestion/tarifas" className="text-primary underline-offset-2 hover:underline">Tarifas</Link>: precio por noche según el día y la temporada, uso individual y 20 € por persona adicional.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-serif">Galería de fotos</CardTitle></CardHeader>
        <CardContent>
          {isNew ? (
            <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              Guarda la habitación para poder subir fotos.
            </p>
          ) : (
            <FotosGaleria habitacionId={id} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

type Foto = { id: string; storage_path: string; alt: string | null; orden: number; url: string };

function FotosGaleria({ habitacionId }: { habitacionId: string }) {
  const [fotos, setFotos] = useState<Foto[]>([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function load() {
    const { data, error } = await supabase
      .from("habitacion_fotos")
      .select("id, storage_path, alt, orden")
      .eq("habitacion_id", habitacionId)
      .order("orden", { ascending: true });
    if (error) return toast.error(error.message);
    const withUrls = await Promise.all(
      (data ?? []).map(async (f) => {
        const { data: s } = await supabase.storage.from("habitaciones").createSignedUrl(f.storage_path, 60 * 60);
        return { ...f, url: s?.signedUrl ?? "" } as Foto;
      }),
    );
    setFotos(withUrls);
  }

  useEffect(() => { load(); }, [habitacionId]);

  async function onFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const maxOrden = fotos.reduce((m, f) => Math.max(m, f.orden), -1);
      let next = maxOrden + 1;
      for (const file of Array.from(files)) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`"${file.name}" supera 5 MB`); continue; }
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
        const path = `${habitacionId}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("habitaciones").upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type });
        if (upErr) { toast.error(upErr.message); continue; }
        const { error: insErr } = await supabase.from("habitacion_fotos").insert({ habitacion_id: habitacionId, storage_path: path, orden: next++ });
        if (insErr) { toast.error(insErr.message); continue; }
      }
      await load();
      if (inputRef.current) inputRef.current.value = "";
      toast.success("Fotos subidas");
    } finally {
      setUploading(false);
    }
  }

  async function remove(f: Foto) {
    if (!confirm("¿Eliminar esta foto?")) return;
    await supabase.storage.from("habitaciones").remove([f.storage_path]);
    const { error } = await supabase.from("habitacion_fotos").delete().eq("id", f.id);
    if (error) return toast.error(error.message);
    load();
  }

  async function move(index: number, dir: -1 | 1) {
    const j = index + dir;
    if (j < 0 || j >= fotos.length) return;
    const a = fotos[index]; const b = fotos[j];
    const { error: e1 } = await supabase.from("habitacion_fotos").update({ orden: b.orden }).eq("id", a.id);
    const { error: e2 } = await supabase.from("habitacion_fotos").update({ orden: a.orden }).eq("id", b.id);
    if (e1 || e2) return toast.error((e1 ?? e2)!.message);
    load();
  }

  async function updateAlt(f: Foto, alt: string) {
    const { error } = await supabase.from("habitacion_fotos").update({ alt }).eq("id", f.id);
    if (error) toast.error(error.message);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">La primera foto será la portada. Máx 5 MB por foto (JPG, PNG o WebP).</p>
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}>
          <ImagePlus className="mr-2 h-4 w-4" />{uploading ? "Subiendo…" : "Añadir fotos"}
        </Button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
      </div>
      {fotos.length === 0 ? (
        <div
          onClick={() => inputRef.current?.click()}
          className="cursor-pointer rounded-md border-2 border-dashed p-10 text-center text-sm text-muted-foreground hover:bg-secondary/50"
        >
          <ImagePlus className="mx-auto mb-2 h-8 w-8" />
          Arrastra o haz clic para subir las primeras fotos
        </div>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {fotos.map((f, i) => (
            <li key={f.id} className="space-y-2 rounded-md border p-2">
              <div className="relative">
                <img src={f.url} alt={f.alt ?? ""} className="aspect-[4/3] w-full rounded object-cover" />
                {i === 0 && (
                  <span className="absolute left-1 top-1 inline-flex items-center gap-1 rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
                    <Star className="h-3 w-3" />Portada
                  </span>
                )}
              </div>
              <Input
                value={f.alt ?? ""}
                placeholder="Texto alternativo"
                onChange={(e) => setFotos((p) => p.map((x) => (x.id === f.id ? { ...x, alt: e.target.value } : x)))}
                onBlur={(e) => updateAlt(f, e.target.value)}
                className="h-8 text-xs"
              />
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, -1)} disabled={i === 0}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, 1)} disabled={i === fotos.length - 1}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => remove(f)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
